import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MessageSquare, Search } from 'lucide-react';
import { Dispatch, Ref, RefObject, SetStateAction, forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ToolType } from '@/modules/common/types';
import theme from '@/styles/theme';

interface MessageInputFieldProps {
  inputMessage: string;
  toolType: ToolType;
  disabledSendButton: boolean;
  disabledToolType: boolean;
  setInputMessage: Dispatch<SetStateAction<string>>;
  sendMessage: (message: string) => void;
  setToolType: Dispatch<SetStateAction<ToolType>>;
}

export const MessageInputField = forwardRef<HTMLTextAreaElement, MessageInputFieldProps>(
  (props, ref: Ref<HTMLTextAreaElement>) => {
    const { sendMessage, disabledSendButton, disabledToolType, toolType, setToolType } = props;
    const { t } = useTranslation();
    const { inputMessage, setInputMessage } = props;
    const [isFocused, setIsFocused] = useState(false);

    const hasInputMessage = inputMessage.length > 0;

    const handleSendMessage = (message: string) => {
      sendMessage(message);
    };

    useEffect(() => {
      const textarea = (ref as RefObject<HTMLTextAreaElement>)?.current;

      const adjustHeight = () => {
        if (textarea) {
          if (inputMessage.length > 0) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
          } else {
            textarea.style.height = '48px'; // Increased from 45px for better vertical centering
          }
        }
      };

      if (textarea) {
        adjustHeight();
        textarea.addEventListener('input', adjustHeight);
        return () => {
          textarea.removeEventListener('input', adjustHeight);
        };
      }
    }, [inputMessage, ref]);

    return (
      <Box position="relative" width="100%">
        <textarea
          ref={ref}
          disabled={disabledSendButton}
          onChange={(e) => setInputMessage(e.currentTarget.value)}
          value={inputMessage}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(inputMessage);
              setInputMessage('');
            }
          }}
          rows={1}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            width: '100%',
            resize: 'none',
            maxHeight: '100px',
            height: 'auto',
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: '1.4375em',
            padding: '14px 16px',
            borderRadius: '4px',
            fontFamily: 'Inter',
            background: '#fff',
            border: `1px solid ${
              isFocused ? theme.palette.primary.main : theme.palette.colors.lightSmokeGrey
            }`,
            outline: 'none',
            paddingRight: '85px',
            overflow: 'auto',
            wordBreak: 'break-word',
          }}
          placeholder={t('modules.chat.chatWindow.placeholder')}
        />
        <Box
          sx={{
            position: 'absolute',
            right: '10px',
            top: '45%', // Changed from 50% to 45%
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            height: '32px',
          }}
        >
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <DropdownMenu>
              <Tooltip
                title={disabledToolType ? t('modules.chat.chatWindow.typeSelectorTooltip') : ''}
              >
                <span>
                  <DropdownMenuTrigger asChild disabled={disabledToolType}>
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.5,
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '28px',
                        width: '28px',
                      }}
                    >
                      {toolType === ToolType.NORMAL ? (
                        <MessageSquare style={{ fontSize: 16, color: '#4356ED' }} />
                      ) : (
                        <Search style={{ fontSize: 16, color: '#8717CC' }} />
                      )}
                    </IconButton>
                  </DropdownMenuTrigger>
                </span>
              </Tooltip>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => setToolType(ToolType.NORMAL)}
                  className={toolType === ToolType.NORMAL ? 'bg-accent' : ''}
                >
                  <MessageSquare className="mr-2 h-4 w-4 text-[#4356ED]" />
                  <span>{t('modules.chat.chatWindow.normalQuestion')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setToolType(ToolType.DEEP_SEARCH)}
                  className={toolType === ToolType.DEEP_SEARCH ? 'bg-accent' : ''}
                >
                  <Search className="mr-2 h-4 w-4 text-[#8717CC]" />
                  <span>{t('modules.chat.chatWindow.deepSearch')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Box>

          <IconButton
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: '4px',
              background: hasInputMessage && !disabledSendButton ? '#4356ED' : 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '28px',
              minWidth: '32px',
            }}
            size="small"
            onClick={() => handleSendMessage(inputMessage)}
            disabled={disabledSendButton}
          >
            <SendIcon
              sx={{
                height: '16px',
                width: '16px',
                fill: hasInputMessage ? '#fff' : undefined,
              }}
            />
          </IconButton>
        </Box>
      </Box>
    );
  }
);
